import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useFamily } from '../context/FamilyContext.jsx';
import { useQuestionnaireData } from '../context/QuestionnaireDataContext.jsx';
import { useRecoveryEmailPrompt } from '../context/RecoveryEmailPromptContext.jsx';
import useAnalytics from '../../hooks/useAnalytics.js';
import CategoryRow from '../components/CategoryRow.jsx';
import QuestionnaireIntroOverlay from '../components/QuestionnaireIntroOverlay.jsx';
import { useEmbeddedNavigation } from '../hooks/useEmbeddedMode.js';
import useOnboardingStatus from '../hooks/useOnboardingStatus.js';
import { Card } from '../ui/Card.jsx';
import Button from '../ui/Button.jsx';
import {
  FaChild, FaUmbrellaBeach, FaFileInvoice, FaUtensils,
  FaBroom, FaPaw, FaCar, FaHouseUser as FaHouse, FaHeartbeat, FaCoins,
  FaList, FaHammer
} from "react-icons/fa";

function getCategoryTypeIcon(label) {
  const l = (label || '').toLowerCase();
  if (l.includes('enfant') || l.includes('bébé')) return FaChild;
  if (l.includes('loisir') || l.includes('vacances') || l.includes('sortie')) return FaUmbrellaBeach;
  if (l.includes('admin') || l.includes('papier') || l.includes('finance') || l.includes('banque')) {
      if (l.includes('finance') || l.includes('banque')) return FaCoins;
      return FaFileInvoice;
  }
  if (l.includes('aliment') || l.includes('repas') || l.includes('course') || l.includes('cuisine')) return FaUtensils;
  if (l.includes('ménage') || l.includes('linge') || l.includes('nettoyage')) return FaBroom;
  if (l.includes('animau') || l.includes('chien') || l.includes('chat')) return FaPaw;
  if (l.includes('véhicule') || l.includes('transport') || l.includes('voiture')) return FaCar;
  if (l.includes('logement') || l.includes('maison') || l.includes('travaux')) return FaHouse;
  if (l.includes('santé') || l.includes('soin') || l.includes('médical')) return FaHeartbeat;

  if (l.includes('bricolage') || l.includes('travaux') || l.includes('construction')) return FaHammer;

  return FaList;
}

export function getIconForCategory(label) {
    return getCategoryTypeIcon(label);
}

export default function QuestionnairePage() {
  const { token, user } = useAuth();
  const { family, members } = useFamily();
  const { answers, ensureQuestionnaireData, error: questionnaireError, questions, refreshAnswers, setAnswers, status: questionnaireStatus } = useQuestionnaireData();
  const { maybeAutoOpenPrompt } = useRecoveryEmailPrompt();
  const { navigateEmbedded } = useEmbeddedNavigation();
  const [error, setError] = useState('');
  const [markingCategoryId, setMarkingCategoryId] = useState(null);
  const [showIntro, setShowIntro] = useState(false);

  const myMember = useMemo(
    () => (members || []).find((m) => m.uuid === user?.uuid) || null,
    [members, user?.uuid]
  );

  useAnalytics({
    currentMember: myMember,
    currentFamily: family
  });

  const {
    error: progressError,
    progress,
    refreshProgress
  } = useOnboardingStatus({
    token,
    family,
    member: myMember
  });

  useEffect(() => {
    ensureQuestionnaireData().catch((e) => setError(e?.message || 'Impossible de charger le questionnaire'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const perCategoryMap = useMemo(() => {
    const map = new Map();
    for (const c of progress?.perCategory || []) {
      map.set(c.categoryId, c);
    }
    return map;
  }, [progress]);

  const hasStartedQuestionnaire = useMemo(
    () => (progress?.perCategory || []).some((category) => category.answeredQuestionsInCategory > 0),
    [progress]
  );

  const hasCompletedCategory = useMemo(
    () => (progress?.perCategory || []).some((category) => category.percent === 100),
    [progress]
  );

  const completedCategoriesCount = useMemo(
    () => (progress?.perCategory || []).filter((category) => category.percent === 100).length,
    [progress]
  );
  const answeredQuestionsCount = useMemo(
    () => Object.keys(answers || {}).length,
    [answers]
  );
  const totalCategoriesCount = questions?.categories?.length || 0;
  const allCategoriesCompleted = totalCategoriesCount > 0 && completedCategoriesCount === totalCategoriesCount;

  const nextCategoryId = useMemo(() => {
    const categories = questions?.categories || [];

    const nextStartedCategory = categories.find((category) => {
      const categoryProgress = perCategoryMap.get(category.id);
      return categoryProgress && categoryProgress.answeredQuestionsInCategory > 0 && categoryProgress.percent < 100;
    });

    const nextFreshCategory = categories.find((category) => {
      const categoryProgress = perCategoryMap.get(category.id);
      return !categoryProgress || categoryProgress.answeredQuestionsInCategory === 0;
    });

    return nextStartedCategory?.id || nextFreshCategory?.id || categories[0]?.id || null;
  }, [perCategoryMap, questions]);

  useEffect(() => {
    maybeAutoOpenPrompt({
      answeredQuestionsCount,
      source: 'questionnaire_overview_threshold'
    });
  }, [answeredQuestionsCount, maybeAutoOpenPrompt]);

  const combinedError = error || questionnaireError || progressError;

  if (combinedError) {
    return (
      <div className="max-w-3xl mx-auto mt-10 p-6">
        <div className="rounded-[24px] border border-red-200/70 bg-red-50/90 p-4 text-red-700 dark:bg-red-900 dark:text-red-200">
          {combinedError}
        </div>
      </div>
    );
  }

  if (!questions || !progress || !answers || questionnaireStatus === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ptcm-primary dark:border-blue-400"></div>
      </div>
    );
  }

  const onMarkCategoryNotRelevant = async (cat) => {
    if (!answers) return;
    setError('');
    setMarkingCategoryId(cat.id);

    try {
      const missing = cat.questions.filter((q) => !answers[q.question_uuid]);
      if (missing.length === 0) {
        setMarkingCategoryId(null);
        return;
      }

      const bulk = missing.map((q) => ({
        question_uuid: q.question_uuid,
        share_score: 0,
        burden_score: 0,
        thought_score: -1
      }));

      // Optimistic fill
      setAnswers((prev) => {
        const next = { ...(prev || {}) };
        for (const a of bulk) {
          next[a.question_uuid] = {
            share_score: a.share_score,
            burden_score: a.burden_score,
            thought_score: a.thought_score
          };
        }
        return next;
      });

      await apiFetch('/api/answers/bulk', { token, method: 'POST', body: { answers: bulk } });
      await refreshProgress().catch(() => undefined);
    } catch (e) {
      setError(e?.message || 'Impossible de mettre à jour la catégorie.');
    } finally {
      setMarkingCategoryId(null);
    }
  };

  const onResetCategory = async (cat) => {
    if (!answers) return;
    setError('');
    setMarkingCategoryId(cat.id);

    const uuids = cat.questions.map((q) => q.question_uuid);

    try {
      await apiFetch('/api/answers/reset', { token, method: 'POST', body: { question_uuids: uuids } });
      await Promise.all([
        refreshAnswers(),
        refreshProgress()
      ]);
    } catch (e) {
      setError(e?.message || 'Impossible de réinitialiser la catégorie.');
      // Best-effort refresh to stay consistent
      try {
        await Promise.all([
          refreshAnswers(),
          refreshProgress()
        ]);
      } catch (err) { }
    } finally {
      setMarkingCategoryId(null);
    }
  };

  const closeIntro = () => setShowIntro(false);

  return (
    <div className="max-w-5xl mx-auto mt-6 px-3 sm:px-4 pb-10 space-y-4">
      <QuestionnaireIntroOverlay open={showIntro} onClose={closeIntro} />

      {!hasStartedQuestionnaire ? (
        <Card className="overflow-hidden">
          <div className="p-5 sm:p-6 space-y-4">
            <div className="space-y-2">
              <div className="ptcm-section-pill">
                Démarrage
              </div>
              <h2 className="text-xl font-semibold text-ptcm-ink dark:text-gray-100">
                Commencez par un premier thème
              </h2>
              <p className="text-sm text-ptcm-muted dark:text-gray-300">
                Vous avancerez ensuite catégorie par catégorie. Vos premiers résultats se débloquent dès qu'un thème est terminé.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={() => navigateEmbedded(nextCategoryId ? `/questionnaire/${nextCategoryId}` : '/questionnaire')}>
                Commencer la première catégorie
              </Button>
              <Button variant="outline" size="lg" onClick={() => setShowIntro(true)}>
                Comment répondre
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {hasStartedQuestionnaire && !hasCompletedCategory ? (
        <Card className="overflow-hidden">
          <div className="p-5 sm:p-6 space-y-4">
            <div className="space-y-2">
              <div className="inline-flex items-center rounded-full border border-amber-200/70 bg-amber-50/90 px-3 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                Presque vos premiers résultats
              </div>
              <h2 className="text-xl font-semibold text-ptcm-ink dark:text-gray-100">
                Terminez un premier thème pour débloquer les stats
              </h2>
              <p className="text-sm text-ptcm-muted dark:text-gray-300">
                Vous avez déjà commencé. Continuez là où vous vous êtes arrêté·e pour obtenir un premier aperçu utile.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={() => navigateEmbedded(nextCategoryId ? `/questionnaire/${nextCategoryId}` : '/questionnaire')}>
                Reprendre le questionnaire
              </Button>
              <Button variant="outline" size="lg" onClick={() => setShowIntro(true)}>
                Revoir les consignes
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {hasCompletedCategory ? (
        <Card className="overflow-hidden border-ptcm-border bg-[linear-gradient(135deg,rgba(213,238,225,0.72)_0%,rgba(255,255,255,0.92)_100%)] dark:border-green-900/30 dark:bg-green-900/10">
          <div className="p-5 sm:p-6 space-y-4">
            <div className="space-y-2">
              <div className="inline-flex items-center rounded-full border border-white/70 bg-white/70 px-3 py-1 text-xs font-semibold text-ptcm-ink dark:bg-green-900/30 dark:text-green-300">
                {allCategoriesCompleted ? 'Questionnaire terminé !' : 'Premiers aperçus disponibles'}
              </div>
              <h2 className="text-xl font-semibold text-ptcm-ink dark:text-gray-100">
                {allCategoriesCompleted
                  ? 'Vous avez tout rempli !'
                  : `Vous avez déjà terminé ${completedCategoriesCount} thème${completedCategoriesCount > 1 ? 's' : ''}`}
              </h2>
              <p className="text-sm text-ptcm-muted dark:text-gray-300">
                {allCategoriesCompleted
                  ? (
                    members.length === 1
                      ? 'Analysez vos statistiques dès maintenant, puis partagez le code de votre foyer à votre conjoint·e si ce n’est pas déjà fait.'
                      : 'Analysez maintenant vos statistiques et comparez-les avec celles de votre conjoint·e.'
                  )
                  : 'Vous pouvez consulter vos premières statistiques dès maintenant, ou continuer pour affiner le diagnostic.'}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" size="lg" onClick={() => navigateEmbedded('/results')}>
                Voir mes statistiques
              </Button>
              {(allCategoriesCompleted && members.length === 1) && (
                <Button size="lg" onClick={() => navigateEmbedded('/')}>
                  Partager avec mon/ma conjoint·e
                </Button>
              )}
            </div>
          </div>
        </Card>
      ) : null}

      <Card>
        <div className="divide-y divide-ptcm-border/70 dark:divide-gray-800">
        {questions.categories
          .map((cat, index) => {
            const p = perCategoryMap.get(cat.id) || {
              percent: 0,
              answeredQuestionsInCategory: 0,
              totalQuestionsInCategory: cat.questions.length
            };

            let group;
            const remaining = p.totalQuestionsInCategory - p.answeredQuestionsInCategory;

            if (p.percent === 100) {
                group = 3; // Finished -> Bottom
            } else if (p.answeredQuestionsInCategory > 0) {
                group = 1; // Started -> Top
            } else {
                group = 2; // Not started -> Middle
            }

            return { cat, p, index, group, remaining };
          })
          .sort((a, b) => {
             // Primary: Group
             if (a.group !== b.group) return a.group - b.group;

             // Secondary:
             // If Started (group 1), sort by remaining questions ascending (least remaining first)
             if (a.group === 1) {
                 if (a.remaining !== b.remaining) return a.remaining - b.remaining;
             }

             // Tertiary: Original index (stable sort)
             return a.index - b.index;
          })
          .map(({ cat, p }) => (
            <CategoryRow
              key={cat.id}
              category={cat}
              progress={p}
              answers={answers}
              isMarking={markingCategoryId === cat.id}
              // icon is now a component rendered by getCategoryIcon, but CategoryRow expects a component type (Icon) not an element (<Icon />)
              // Wait, getCategoryIcon now returns JSX. CategoryRow uses it as {Icon && <div...><Icon ... /></div>}
              // Let's check CategoryRow
              // CategoryRow: function NavItem({ to, label, icon: Icon, active }) ... <Icon ... />
              // Ah wait, CategoryRow code I wrote earlier:
              // function CategoryRow({ ..., icon: Icon }) ... {Icon && ... <Icon className=... />}
              // So it expects a Component Type (e.g. FaChild), NOT an element (<FaChild />).
              // My previous change to getCategoryIcon made it return Elements.
              // I should revert getCategoryIcon to return types, OR update CategoryRow to accept Elements.
              // Updating CategoryRow is safer if I want to pre-configure them. But returning types is cleaner for the Icon prop pattern.
              // Let's revert getCategoryIcon to return types, and export it for StatsPage.
              icon={getCategoryTypeIcon(cat.label)}
              onOpen={() => navigateEmbedded(`/questionnaire/${cat.id}`)}
              onMarkNotRelevant={() => onMarkCategoryNotRelevant(cat)}
              onResetCategory={() => onResetCategory(cat)}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}

